import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import "./index.css";
import {
  Database,
  Palette,
  Globe,
  Mic,
  Languages,
  Target,
  Sparkles,
  BarChart3,
} from "lucide-react";
import { ChatWidget } from "@/components/chat_widget/ChatWidget";

export function App() {
  const scrollToFeatures = () => {
    document.getElementById("features")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-white/80 backdrop-blur-sm">
        <div className="container mx-auto flex h-16 items-center justify-between px-4 md:px-6">
          <div className="flex items-center gap-2">
            <div className="text-2xl font-bold text-[#2A1745]">
              bubble<span className="text-[#D14081]">marble</span>
            </div>
          </div>
          <Button onClick={scrollToFeatures} variant="ghost">
            Features
          </Button>
        </div>
      </header>

      {/* Hero Section with Chat */}
      <section className="relative overflow-hidden">
        {/* Background Image with Blur */}
        <div 
          className="absolute inset-0 z-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(/public/background.jpg)' }}
        >
          <div className="absolute inset-0 backdrop-blur-sm bg-white/60" />
        </div>

        {/* Content */}
        <div className="container relative z-10 mx-auto px-4 py-12 md:px-6 md:py-20">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            {/* Left side - Content */}
            <div className="flex flex-col justify-center space-y-6">
            <div className="space-y-4">
              <h1 className="text-4xl font-bold tracking-tight text-[#2A1745] md:text-5xl lg:text-6xl">
                AI Chat That Understands Your Business
              </h1>
                <p className="text-lg text-gray-600 md:text-xl">
                  Transform how your customers interact with your platform. Natural conversations that drive real results.
                </p>
              </div>
            <div className="flex flex-col gap-4 sm:flex-row">
              <Button
                size="lg"
                className="bg-gradient-to-r from-[#D14081] to-[#2A1745] hover:opacity-90"
                onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" })}
              >
                Get Started
              </Button>
                <Button size="lg" variant="outline" onClick={scrollToFeatures}>
                  Learn More
                </Button>
              </div>
            </div>

            {/* Right side - Live Chat Widget */}
            <div className="flex items-center justify-center">
              <div className="w-full max-w-md">
                <ChatWidget embedded />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="border-t bg-white py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mb-12 space-y-4 text-center">
            <h2 className="text-3xl font-bold tracking-tight text-[#2A1745] md:text-4xl">
              Built for Growth, Designed for Results
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-gray-600">
              Every conversation is an opportunity. Our AI chat doesn't just answer questions—it understands context, learns from your data, and turns interactions into conversions.
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {/* Feature 1 */}
            <Card className="border-2 transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-[#DED1F0]">
                  <Database className="h-6 w-6 text-[#2A1745]" />
                </div>
                <h3 className="mb-2 text-xl font-semibold text-[#2A1745]">
                  Your Data, Your Power
                </h3>
                <p className="text-gray-600">
                  Imagine your customers finding exactly what they need by simply asking. Our AI integrates directly with your database, transforming complex queries into natural conversations. No more endless scrolling—just instant, accurate answers that feel personal.
                </p>
              </CardContent>
            </Card>

            {/* Feature 2 */}
            <Card className="border-2 transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-[#DED1F0]">
                  <Globe className="h-6 w-6 text-[#2A1745]" />
                </div>
                <h3 className="mb-2 text-xl font-semibold text-[#2A1745]">
                  Connects Everything
                </h3>
                <p className="text-gray-600">
                  Your business doesn't run on a single system, and neither should your AI. Seamlessly connect with your CRM, APIs, and CMS. Whether it's Salesforce, HubSpot, or your custom platform, we make integration effortless.
                </p>
              </CardContent>
            </Card>

            {/* Feature 3 */}
            <Card className="border-2 transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-[#DED1F0]">
                  <Palette className="h-6 w-6 text-[#2A1745]" />
                </div>
                <h3 className="mb-2 text-xl font-semibold text-[#2A1745]">
                  Your Brand, Your Style
                </h3>
                <p className="text-gray-600">
                  This isn't another generic chatbot. Customize every color, font, and interaction to match your brand identity. Your customers should feel at home, not like they're talking to someone else's tool.
                </p>
              </CardContent>
            </Card>

            {/* Feature 4 */}
            <Card className="border-2 transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-[#DED1F0]">
                  <Sparkles className="h-6 w-6 text-[#2A1745]" />
                </div>
                <h3 className="mb-2 text-xl font-semibold text-[#2A1745]">
                  Real-Time Intelligence
                </h3>
                <p className="text-gray-600">
                  Why limit your AI to what's in your database? Our chat searches the internet in real-time, combining your business knowledge with up-to-date facts. Your customers get comprehensive answers, every time.
                </p>
              </CardContent>
            </Card>

            {/* Feature 5 */}
            <Card className="border-2 transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-[#DED1F0]">
                  <Mic className="h-6 w-6 text-[#2A1745]" />
                </div>
                <h3 className="mb-2 text-xl font-semibold text-[#2A1745]">
                  Voice That Works
                </h3>
                <p className="text-gray-600">
                  Sometimes typing isn't convenient. Whether your customer is driving, cooking, or multitasking, voice chat keeps the conversation flowing. Natural speech recognition that actually understands accents and context.
                </p>
              </CardContent>
            </Card>

            {/* Feature 6 */}
            <Card className="border-2 transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-[#DED1F0]">
                  <Languages className="h-6 w-6 text-[#2A1745]" />
                </div>
                <h3 className="mb-2 text-xl font-semibold text-[#2A1745]">
                  Speaks Their Language
                </h3>
                <p className="text-gray-600">
                  Your customers come from everywhere. Our multi-language support doesn't just translate words—it understands cultural nuances and context. One AI, unlimited markets.
                </p>
              </CardContent>
            </Card>

            {/* Feature 7 */}
            <Card className="border-2 transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-[#DED1F0]">
                  <Target className="h-6 w-6 text-[#2A1745]" />
                </div>
                <h3 className="mb-2 text-xl font-semibold text-[#2A1745]">
                  Converts While It Chats
                </h3>
                <p className="text-gray-600">
                  Every conversation should move your business forward. Our AI guides users to action—completing orders, scheduling calls, capturing leads. It's not just support; it's your best sales assistant working 24/7.
                </p>
              </CardContent>
            </Card>

            {/* Feature 8 */}
            <Card className="border-2 transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-[#DED1F0]">
                  <Sparkles className="h-6 w-6 text-[#2A1745]" />
                </div>
                <h3 className="mb-2 text-xl font-semibold text-[#2A1745]">
                  Works Anywhere
                </h3>
                <p className="text-gray-600">
                  E-commerce, real estate, fintech, support—whatever your domain, our AI adapts. We've built intelligence that understands your industry's unique needs and speaks your customers' language.
                </p>
              </CardContent>
            </Card>

            {/* Feature 9 - Chat Analytics */}
            <Card className="border-2 transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-[#DED1F0]">
                  <BarChart3 className="h-6 w-6 text-[#2A1745]" />
      </div>
                <h3 className="mb-2 text-xl font-semibold text-[#2A1745]">
                  Know Your Customers
                </h3>
                <p className="text-gray-600">
                  What if you could see exactly what your customers are asking for? Our analytics dashboard reveals chat sessions, message volumes, conversation patterns, and full chat history. Turn insights into strategy and watch your conversion rates climb. Every conversation tells a story—we help you read it.
                </p>
        </CardContent>
      </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="border-t bg-gradient-to-r from-[#D14081] to-[#2A1745] py-16 text-white">
        <div className="container mx-auto px-4 text-center md:px-6">
          <h2 className="mb-4 text-3xl font-bold md:text-4xl">
            Ready to Transform Your Customer Experience?
          </h2>
          <p className="mb-8 text-lg text-white/90">
            Join leading businesses that trust our AI to drive growth and customer satisfaction.
          </p>
          <Button size="lg" variant="secondary" className="bg-white text-[#2A1745] hover:bg-white/90">
            Start Your Project
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t bg-white py-8">
        <div className="container mx-auto px-4 text-center text-sm text-gray-600 md:px-6">
          <p>
            Bubble Marble LTD © {new Date().getFullYear()} | Building digital products that drive growth
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
